import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { appendPath } from '@truqc/content-path';
import AddAssetButton from './components/addAssetButton';
import AddButton from './components/addButton';
import AddDocumentButton from './components/addDocumentButton';
import AddFromPreviousButton from './components/addFromPreviousButton';
import AddSignatureButton from './components/addSignatureButton';
import ArcGisMap from './components/arcGisMap';
import AssetPickerField from './components/assetPickerField';
import BehaviorButton from './components/behaviorButton';
import EditDocumentButton from './components/editDocumentButton';
import HeaderBar from './components/headerBar';
/* eslint-disable-next-line import/no-cycle */
import Map from './components/map/map';
import MapTypePicker from './components/map/mapTypePicker';
import MapView from './components/map/mapView';
import ModalButton from './components/modalButton';
import NumberField from './components/numberField';
import UnitsField from './components/unitsField';
import TextField from './components/textField';
import Text from './components/text';
import TextView from './components/textView';
import DatePickerField from './components/datePickerField'; // Date is a reserved word
import DateTimePickerField from './components/dateTimePickerField';
import DuplicateButton from './components/duplicateButton';
import DeprecatedFormPickerField from './components/deprecatedFormPickerField';
import TimePickerField from './components/timePickerField';
import PiecesPickerField from './components/piecesPickerField';
/* eslint-disable-next-line import/no-cycle */
import GridCol from './gridCol';
import SignatureField from './components/signatureField';
import SinglePickerField from './components/singlePickerField';
import OptionPickerButton from './components/optionPickerButton';
import OptionPickerField from './components/optionPickerField';
import PhaseCodePickerField from './components/phaseCodePickerField';
import PhotoPickerField from './components/photoContainer';
import ColorPickerField from './components/colorPickerField';
import RemoveButton from './components/removeButton';
import ReportPickerField from './components/reportPickerField';
import WeekEndingField from './components/weekEndingField';
import SinglePickerButton from './components/singlePickerButton';
import NewIssueButton from './components/newIssueButton';
/* eslint-disable-next-line import/no-cycle */
import Divider from './components/divider';
import LabelView from './components/labelView';
import issueSummaryPicker from './components/issueSummaryPicker';
import FillFromPreviousButton from './components/fillFromPreviousButton';
import ComponentCodePicker from './components/componentCodePicker';
import UserSinglePickerField from './components/userSinglePickerField';
// job specific components
import JobAssociatedAssetsSubsection from './components/job/jobAssociatedAssetsSubsection';
import JobComponentsSubsection from './components/job/jobComponentsSubsection';
import JobContractRequirementsSubsection from './components/job/jobContractRequirementsSubsection';
import JobFlangeValveInspectionResultsSubsection from './components/job/jobFlangeValveInspectionResultsSubsection';
import JobFlangeValveInformationSubsection from './components/job/jobFlangeValveInformationSubsection';
import JobGeneralSubsection from './components/job/jobGeneralSubsection';
import JobPhaseCodesSubsection from './components/job/jobPhaseCodesSubsection';
import JobPiecesSubsection from './components/job/jobPiecesSubsection';
import OptionActionsButton from './components/optionActionsButton/optionActionsButton';
import Column from './column';
import SelectAndParseCSV from './components/selectAndParseCSV';
import Container from './container';
import ProcessingIndicator from './components/processingIndicator';
import UploadField from './components/uploadField';
import PDFViewer from './components/pdfViewer';
import MediaViewer from './components/mediaViewer';
import QualityAgentView from './components/qualityAgentView/qualityAgentView';
import AuditTrailViewer from './components/auditTrailViewer/auditTrailViewer';
import TvcIssuesSection from './components/tvcIssuesSection/tvcIssuesSection';
import TableView from './components/tableView';

// these components will handle format.children on their own.
const components = {
  addButton: AddButton,
  addAssetButton: AddAssetButton,
  modalButton: ModalButton,
  addSignatureButton: AddSignatureButton,
  addDocumentButton: AddDocumentButton,
  addFromPreviousButton: AddFromPreviousButton,
  addPhotosButton: AddButton,
  arcGisMap: ArcGisMap,
  assetPickerField: AssetPickerField,
  behaviorButton: BehaviorButton,
  colorPickerField: ColorPickerField,
  column: GridCol,
  container: GridCol,
  v1Container: GridCol,
  subsection: GridCol,
  datePickerField: DatePickerField,
  // Date is a reserved word
  dateTimePickerField: DateTimePickerField,
  deprecatedFormPickerField: DeprecatedFormPickerField,
  divider: Divider,
  duplicateButton: DuplicateButton,
  editDocumentButton: EditDocumentButton,
  // Once the `deprecatedFormPickerField` is in place as an alias for `formPickerField`,
  // we can switch over instances of `formPickerField` in config to `deprecatedFormPickerField`
  // and remove the following line:
  formPickerField: DeprecatedFormPickerField,
  formSearchField: OptionPickerField,
  optionSearchField: OptionPickerField,
  headerBar: HeaderBar,
  fillFromPreviousButton: FillFromPreviousButton,
  issueSummaryPickerField: issueSummaryPicker,
  /* we might consider configuring the EditorComponent to include only components that
      are relevant for the given entity, e.g. job, report, or issue.  For now, I just followed
      our same precendent of adding everything in */
  jobAssociatedAssetsSubsection: JobAssociatedAssetsSubsection,
  jobComponentsSubsection: JobComponentsSubsection,
  jobContractRequirementsSubsection: JobContractRequirementsSubsection,
  jobFlangeValveInspectionResultsSubsection: JobFlangeValveInspectionResultsSubsection,
  jobFlangeValveInformationSubsection: JobFlangeValveInformationSubsection,
  jobGeneralSubsection: JobGeneralSubsection,
  jobPhaseCodesSubsection: JobPhaseCodesSubsection,
  jobPiecesSubsection: JobPiecesSubsection,
  labelView: LabelView,
  map: Map,
  mapTypePicker: MapTypePicker,
  mapView: MapView,
  numberField: NumberField,
  optionPickerButton: OptionPickerButton,
  // schema2.0
  optionPickerField: OptionPickerField,
  // optionPickerButton: OptionPickerField,
  optionActionsButton: OptionActionsButton,
  pdfViewer: PDFViewer,
  mediaViewer: MediaViewer,
  phaseCodePickerField: PhaseCodePickerField,
  // lookup
  photoPickerField: PhotoPickerField,
  piecesPickerField: PiecesPickerField,
  removeButton: RemoveButton,
  reportPickerField: ReportPickerField,
  row: GridCol,
  issueComponentPickerField: ComponentCodePicker,
  signatureField: SignatureField,
  singlePickerButton: SinglePickerButton,
  singlePickerField: SinglePickerField,
  section: GridCol,
  textField: TextField,
  text: Text,
  textView: TextView,
  timePickerField: TimePickerField,
  unitsField: UnitsField,
  userSinglePickerField: UserSinglePickerField,
  newIssueButton: NewIssueButton,
  weekEndingField: WeekEndingField,
  selectAndParseCSV: SelectAndParseCSV,
  processingIndicator: ProcessingIndicator,
  uploadField: UploadField,
  qualityAgentView: QualityAgentView,
  auditTrailViewer: AuditTrailViewer,
  tvcIssuesSection: TvcIssuesSection,
  tableView: TableView
};
const EditorComponent = _ref => {
  let format = _ref.format,
    onChange = _ref.onChange,
    reduxEntity = _ref.reduxEntity,
    operationComplete = _ref.operationComplete,
    operationPending = _ref.operationPending,
    _ref$minControlWidth = _ref.minControlWidth,
    minControlWidth = _ref$minControlWidth === void 0 ? 0 : _ref$minControlWidth;
  // accept either children as props or create from format
  const getChildren = useCallback(function (parentFormat) {
    let parentContentPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '/';
    let formatPath = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '/';
    let childComponents = null;
    const collapsible = parentFormat.collapsible,
      children = parentFormat.children,
      label = parentFormat.label,
      type = parentFormat.type,
      labelContentPath = parentFormat.labelContentPath,
      contentPath = parentFormat.contentPath,
      parentVariables = parentFormat.variables;
    if (children !== undefined) {
      const childrenFromFormat = JSON.parse(JSON.stringify(children));
      childComponents = childrenFromFormat.map((child, index) => {
        const ChildComponentImpl = components[child.type];
        const key = "editorComponent".concat(index);
        const childFormat = JSON.parse(JSON.stringify(child));
        const childFormatPath = appendPath(formatPath, "children[".concat(index, "]"));
        const childContentPath = appendPath(parentContentPath, childFormat.contentPath);
        childFormat.parentContentPath = parentContentPath;
        // merge the parent and child context - child overrides should spread over parent
        childFormat.variables = _objectSpread(_objectSpread({}, parentVariables), childFormat.variables);

        // recursively render children here.
        const newChildren = childFormat.children === undefined ? null : getChildren(childFormat, childContentPath, childFormatPath);
        const mdWidth = childFormat.mdWidth,
          mdPadding = childFormat.mdPadding,
          webWidth = childFormat.web_mdWidth,
          childFormatType = childFormat.type;
        if (ChildComponentImpl === undefined) {
          // eslint-disable-next-line no-console
          console.warn("NOT rendering child ".concat(childFormatType));
          return null;
        }
        delete childFormat.children;
        childFormat.componentType = childFormatType;
        return /*#__PURE__*/React.createElement(Column, {
          key: key,
          minControlWidth: minControlWidth,
          mdWidth: mdWidth,
          web_mdWidth: webWidth,
          mdPadding: mdPadding,
          formatPath: childFormatPath,
          className: format.type,
          componentType: childFormatType
        }, /*#__PURE__*/React.createElement(ChildComponentImpl
        // eslint-disable-next-line react/jsx-props-no-spreading
        , _extends({}, childFormat, {
          // Refactor to be explicit and avoid prop drilling
          parentContentPath: parentContentPath,
          format: childFormat,
          onChange: onChange,
          reduxEntity: reduxEntity,
          formatPath: childFormatPath,
          operationComplete: operationComplete,
          operationPending: operationPending
        }), newChildren));
      });
    }
    const containerFormat = _objectSpread(_objectSpread({}, parentFormat), {}, {
      componentType: type
    });
    return /*#__PURE__*/React.createElement(Container, {
      className: "t-gc-title",
      label: label,
      labelContentPath: labelContentPath,
      collapsible: collapsible,
      formatType: type,
      contentPath: contentPath,
      parentContentPath: parentContentPath,
      reduxEntity: reduxEntity,
      format: containerFormat
    }, childComponents);
  }, [format.type, minControlWidth, onChange, operationComplete, operationPending, reduxEntity]);
  const contentPath = useMemo(() => format.contentPath ? "/".concat(format.contentPath) : '/', [format.contentPath]);
  const displayChildren = useMemo(() => {
    // for sections, we do not want to display a label on the form.
    // so, remove it.
    const sectionFormat = JSON.parse(JSON.stringify(format));
    delete sectionFormat.label;
    return getChildren(sectionFormat, contentPath);
  }, [contentPath, format, getChildren]);
  // render the section.
  return /*#__PURE__*/React.createElement(Column, {
    key: "Section ".concat(format.label),
    formatPath: "/",
    format: format
  }, /*#__PURE__*/React.createElement(GridCol, {
    parentContentPath: contentPath,
    format: format // could rewrite gridCol to take props from above instead of passing in
    ,
    onChange: onChange,
    reduxEntity: reduxEntity
  }, displayChildren));
};
EditorComponent.propTypes = {
  format: PropTypes.shape({
    label: PropTypes.string,
    contentPath: PropTypes.string,
    postActions: PropTypes.arrayOf(PropTypes.shape({})),
    preActions: PropTypes.arrayOf(PropTypes.shape({})),
    postEvents: PropTypes.arrayOf(PropTypes.string),
    preEvents: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
    visible: PropTypes.bool
  }).isRequired,
  minControlWidth: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  reduxEntity: PropTypes.string.isRequired,
  operationPending: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired
};
export default EditorComponent;